import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { formatMoney } from '../utils/formatters';
import { normalizeInternationalPhone, sanitizePhoneForTel } from '../utils/phone';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { buildShareUrl, shareNative, copyText } from '../utils/shareUtils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, Heart, MapPin, MessageCircle, Phone, Flag, Download,
  ShoppingCart, Clock, Share2, Eye, Bookmark, FileText,
  ChevronRight, X, ChevronLeft, ChevronUp, ChevronDown, ZoomIn,
  Star, GraduationCap, BookOpen, Award, Mail, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import SellerInfoCard from '../components/SellerInfoCard';
import BookContactEditor from '../components/BookContactEditor';
import AppHeader from '../components/AppHeader';
import '../components/SellerInfoCard.css';
import './BookDetails.css';

const TYPE_CONFIG = {
  free: { label_en: 'Free', label_ar: 'مجاني', color: 'emerald', icon: Download },
  sell: { label_en: 'For Sale', label_ar: 'للبيع', color: 'blue', icon: ShoppingCart },
  rent: { label_en: 'For Rent', label_ar: 'للإيجار', color: 'amber', icon: Clock },
  download: { label_en: 'Download', label_ar: 'تحميل', color: 'violet', icon: Download },
};

const CTA_CONFIG = {
  free: { label_en: 'Get Free', label_ar: 'احصل مجانا' },
  sell: { label_en: 'Buy Now', label_ar: 'اشتر الآن' },
  rent: { label_en: 'Rent Now', label_ar: 'استاجر الان' },
  download: { label_en: 'Download', label_ar: 'تحميل' },
  // Digital/Physical overrides
  download_free: { label_en: 'Download Free', label_ar: 'تحميل مجاني' },
  contact_seller: { label_en: 'Contact Seller', label_ar: 'تواصل مع البائع' },
};

const TAG_TO_TYPE = {
  past_exam: { en: 'Past Papers', ar: 'أسئلة سابقة' },
  lecture_notes: { en: 'Lecture Notes', ar: 'مذكرات' },
  notes: { en: 'Notes', ar: 'ملخصات' },
  textbook: { en: 'Textbook', ar: 'كتاب دراسي' },
  lab_manual: { en: 'Lab Manual', ar: 'دليل عملي' },
  summary: { en: 'Summary', ar: 'ملخص' },
  slides: { en: 'Slides', ar: 'شرائح' },
  novels: { en: 'Novels', ar: 'روايات' },
  book: { en: 'Book', ar: 'كتاب' },
  other: { en: 'Other', ar: 'أخرى' },
};

const TAG_LABELS = {
  computer_science: { en: 'Computer Science', ar: 'علوم الحاسوب' },
  programming: { en: 'Programming', ar: 'برمجة' },
  algorithms: { en: 'Algorithms', ar: 'خوارزميات' },
  physics: { en: 'Physics', ar: 'الفيزياء' },
  tawjihi: { en: 'Tawjihi', ar: 'توجيهي' },
  secondary: { en: 'Secondary', ar: 'ثانوي' },
  medicine: { en: 'Medicine', ar: 'الطب' },
  biochemistry: { en: 'Biochemistry', ar: 'كيمياء حيوية' },
  medical: { en: 'Medical', ar: 'طبي' },
  engineering: { en: 'Engineering', ar: 'الهندسة' },
  math: { en: 'Math', ar: 'الرياضيات' },
  arabic: { en: 'Arabic', ar: 'عربي' },
  literature: { en: 'Literature', ar: 'أدب' },
  finance: { en: 'Finance', ar: 'مالية' },
  mba: { en: 'MBA', ar: 'ماجستير إدارة أعمال' },
  electrical: { en: 'Electrical', ar: 'كهرباء' },
  wiring: { en: 'Wiring', ar: 'توصيلات' },
  chemistry: { en: 'Chemistry', ar: 'الكيمياء' },
  lab: { en: 'Lab', ar: 'معمل' },
  accounting: { en: 'Accounting', ar: 'المحاسبة' },
  acca: { en: 'ACCA', ar: 'ACCA' },
  biology: { en: 'Biology', ar: 'الأحياء' },
  civil: { en: 'Civil', ar: 'مدني' },
  mechanics: { en: 'Mechanics', ar: 'ميكانيكا' },
  islamic: { en: 'Islamic Studies', ar: 'دراسات إسلامية' },
  university: { en: 'University', ar: 'جامعي' },
  pharmacy: { en: 'Pharmacy', ar: 'الصيدلة' },
  dentistry: { en: 'Dentistry', ar: 'طب الأسنان' },
  nursing: { en: 'Nursing', ar: 'التمريض' },
  civil_eng: { en: 'Civil Engineering', ar: 'الهندسة المدنية' },
  electrical_eng: { en: 'Electrical Engineering', ar: 'الهندسة الكهربائية' },
  mechanical_eng: { en: 'Mechanical Engineering', ar: 'الهندسة الميكانيكية' },
  it: { en: 'Information Technology', ar: 'تقنية المعلومات' },
  data: { en: 'Data Science', ar: 'علم البيانات' },
  ai: { en: 'Artificial Intelligence', ar: 'الذكاء الاصطناعي' },
  business: { en: 'Business Administration', ar: 'إدارة الأعمال' },
  economics: { en: 'Economics', ar: 'الاقتصاد' },
  marketing: { en: 'Marketing', ar: 'التسويق' },
  law: { en: 'Law', ar: 'القانون' },
  education: { en: 'Education', ar: 'التربية' },
  arts: { en: 'Arts & Design', ar: 'الفنون والتصميم' },
  languages: { en: 'Languages & Translation', ar: 'اللغات والترجمة' },
  agriculture: { en: 'Agriculture', ar: 'الزراعة' },
  environment: { en: 'Environmental Science', ar: 'علوم البيئة' },
  cs: { en: 'Computer Science', ar: 'علوم الحاسوب' },
  other: { en: 'Other', ar: 'أخرى' },
};

function getContentTypeLabel(book, isRTL) {
  const lang = isRTL ? 'ar' : 'en';
  if (book.type && TAG_TO_TYPE[book.type]) return TAG_TO_TYPE[book.type][lang];
  if (book.tags?.length) {
    for (const tag of book.tags) {
      if (TAG_TO_TYPE[tag]) return TAG_TO_TYPE[tag][lang];
    }
  }
  return isRTL ? 'مستند' : 'Document';
}

function getTagLabel(tag, isRTL) {
  if (TAG_LABELS[tag]) return TAG_LABELS[tag][isRTL ? 'ar' : 'en'];
  // Fallback: capitalize and replace underscores
  return tag.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function BookDetails() {
  const { bookId } = useParams();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = i18n.language || 'ar';
  const isRTL = lang === 'ar' || document.dir === 'rtl';

  const [book, setBook] = useState(null);
  const [seller, setSeller] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [relatedBooks, setRelatedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [showReportConfirm, setShowReportConfirm] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [ratings, setRatings] = useState([]);
  const [ratingStats, setRatingStats] = useState({ average: 0, count: 0 });
  const [userRating, setUserRating] = useState(0);
  const [userReview, setUserReview] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);

  // Seller listings (inline expand)
  const [sellerListings, setSellerListings] = useState([]);
  const [showSellerListings, setShowSellerListings] = useState(false);
  const [loadingSellerListings, setLoadingSellerListings] = useState(false);
  const sellerListingsRef = useRef(null);

  // Contact sheet
  const [showContactSheet, setShowContactSheet] = useState(false);
  const [contact, setContact] = useState(null);
  const [showContactEditor, setShowContactEditor] = useState(false);

  useEffect(() => {
    fetchBook();
    fetchRelated();
    fetchRatings();
    if (user && token) checkSaved();
  }, [bookId]);

  const fetchBook = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/item/${bookId}`);
      if (res.ok) {
        const data = await res.json();
        setBook({ ...data.item, book_id: data.item.id });
        setSeller(data.seller);
        setMetrics(data.metrics);
        setContact(data.contact || null);
      } else {
        const res2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/books/${bookId}`);
        if (res2.ok) {
          const data = await res2.json();
          setBook(data.book);
          setSeller(data.seller);
          setContact(data.contact || null);
        } else {
          navigate('/student-hub/books');
        }
      }
    } catch (e) {
      console.error(e);
      navigate('/student-hub/books');
    }
    setLoading(false);
  };

  const fetchRelated = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/item/${bookId}/related?limit=4`);
      if (res.ok) {
        const data = await res.json();
        setRelatedBooks(data.items || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchRatings = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/books/${bookId}/ratings`);
      if (res.ok) {
        const data = await res.json();
        setRatings(data.ratings || []);
        setRatingStats({ average: data.average_rating || 0, count: data.total_count || 0 });
      }
    } catch (e) { console.error(e); }
  };

  const submitRating = async () => {
    if (!userRating || !user || !token) return;
    setSubmittingRating(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/books/${bookId}/rate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: userRating, review: userReview || null })
      });
      if (res.ok) {
        toast.success(t('bookdeta.ratingSubmitted'));
        setUserReview('');
        fetchRatings();
      }
    } catch (e) { console.error(e); }
    setSubmittingRating(false);
  };

  const checkSaved = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items?item_type=book`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsSaved(data.saved_items?.some(s => s.item_id === bookId) || false);
      }
    } catch (e) { console.error(e); }
  };

  const toggleSave = async () => {
    if (!user || !token) { navigate('/welcome'); return; }
    try {
      if (isSaved) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items/book/${bookId}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        });
        setIsSaved(false);
        toast.success(isRTL ? 'تم الإزالة من المحفوظات' : 'Removed from saved');
      } else {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_type: 'book', item_id: bookId })
        });
        setIsSaved(true);
        toast.success(isRTL ? 'تم الحفظ' : 'Saved!');
      }
    } catch (e) { console.error(e); }
  };

  const handleShare = async () => {
    const url = buildShareUrl(`/student-hub/books/${bookId}`);
    const title = book?.title || 'Book';
    const res = await shareNative({ title, url });
    if (res.completed) return;
    try {
      await copyText(url);
      toast.success(isRTL ? 'تم نسخ الرابط' : 'Link copied!');
    } catch (e) {
      toast.info(url);
    }
  };

  const handleCTA = () => {
    if (!user || !token) {
      navigate('/welcome', { state: { from: `/student-hub/books/${bookId}` } });
      return;
    }

    const itemType = book?.item_type;
    const isFreeOrDownload = mode === 'free' || mode === 'download';

    // Digital items: trigger download
    if (itemType === 'digital' && isFreeOrDownload) {
      if (book?.file_url) {
        const url = book.file_url.startsWith('http')
          ? book.file_url
          : `${process.env.REACT_APP_BACKEND_URL}${book.file_url}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(isRTL ? 'جاري التحميل...' : 'Downloading...');
      } else {
        toast.error(isRTL ? 'الملف غير متوفر بعد' : 'File not available yet');
      }
      return;
    }

    // Physical items (free): open contact sheet
    if (isFreeOrDownload) {
      handleMessageSeller();
      return;
    }

    // Paid items: open contact sheet to negotiate purchase
    handleMessageSeller();
  };

  // -- Seller listings: fetch and toggle inline --
  const toggleSellerListings = useCallback(async () => {
    if (showSellerListings) {
      setShowSellerListings(false);
      return;
    }
    if (!seller?.user_id) return;
    setLoadingSellerListings(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/seller/${seller.user_id}/listings?limit=8`);
      if (res.ok) {
        const data = await res.json();
        // Filter out the current book
        const others = (data.items || []).filter(item => item.id !== bookId);
        setSellerListings(others);
      }
    } catch (e) { console.error(e); }
    setLoadingSellerListings(false);
    setShowSellerListings(true);
    // Smooth scroll after render
    setTimeout(() => {
      sellerListingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [showSellerListings, seller, bookId]);

  // -- Listing-level contact info (per-listing methods the seller enabled).
  // Falls back to legacy fields for seed items / old listings.
  const contactInfo = contact || {
    chat: true,
    phone: ['phone', 'both', 'chat_phone'].includes(book?.contact_method) ? (book?.phone || null) : null,
    whatsapp: ['whatsapp', 'chat_whatsapp'].includes(book?.contact_method)
      ? (book?.phone || seller?.whatsapp_phone || null)
      : (seller?.whatsapp_phone || null),
    email: null,
  };
  const callNorm = contactInfo.phone ? normalizeInternationalPhone(contactInfo.phone) : null;
  const callTel = contactInfo.phone ? (callNorm?.valid ? callNorm.e164 : sanitizePhoneForTel(contactInfo.phone)) : '';
  const callDisplay = callNorm?.valid ? callNorm.display : (contactInfo.phone || '');
  const waNorm = contactInfo.whatsapp ? normalizeInternationalPhone(contactInfo.whatsapp) : null;
  // If a seller-enabled WhatsApp number can't be normalized (legacy/foreign
  // format without "+"), fall back to its raw digits so the option never
  // silently disappears — wa.me accepts full international digit strings.
  const waRawDigits = contactInfo.whatsapp ? String(contactInfo.whatsapp).replace(/\D/g, '') : '';
  const waDigits = waNorm?.valid ? waNorm.waDigits : (waRawDigits.length >= 8 ? waRawDigits : '');
  const waDisplay = waNorm?.valid ? waNorm.display : (contactInfo.whatsapp || '');
  const hasCall = Boolean(callTel);
  const hasWhatsApp = Boolean(waDigits);
  const hasEmail = Boolean(contactInfo.email);
  const hasChat = contactInfo.chat !== false;
  const isOwner = Boolean(user?.user_id && seller?.user_id && user.user_id === seller.user_id);

  // -- Contact / Message handler --
  const handleMessageSeller = useCallback(() => {
    if (!user) { navigate('/welcome'); return; }
    if (!seller) return;
    // Always show contact options sheet so user can choose
    setShowContactSheet(true);
  }, [user, seller, navigate]);

  const openWasslaChat = useCallback(() => {
    if (!seller) return;
    setShowContactSheet(false);

    const bookTitle = isRTL && book?.title_ar ? book.title_ar : book?.title || '';
    navigate('/messages/new', {
      state: {
        fromBookId: bookId,
        sellerId: seller.user_id,
        sellerName: seller.name || 'Seller',
        adTitle: bookTitle,
      },
    });
  }, [seller, navigate, isRTL, book, bookId]);

  const openWhatsApp = useCallback(() => {
    setShowContactSheet(false);
    if (!waDigits) return;
    const bookTitle = book?.title || '';
    const text = encodeURIComponent(
      isRTL ? `مرحبا، أنا مهتم بـ "${bookTitle}" على وصلة` : `Hi, I'm interested in "${bookTitle}" on Wassla`
    );
    window.open(`https://wa.me/${waDigits}?text=${text}`, '_blank');
  }, [waDigits, book, isRTL]);

  const openCall = useCallback(() => {
    setShowContactSheet(false);
    if (callTel) window.location.href = `tel:${callTel}`;
  }, [callTel]);

  const openEmail = useCallback(() => {
    setShowContactSheet(false);
    if (!contactInfo.email) return;
    const bookTitle = book?.title || '';
    const subject = encodeURIComponent((isRTL ? 'استفسار عن: ' : 'Inquiry about: ') + bookTitle);
    const body = encodeURIComponent(isRTL ? `مرحبا، أنا مهتم بـ "${bookTitle}" على وصلة` : `Hi, I'm interested in "${bookTitle}" on Wassla`);
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  }, [contactInfo.email, book, isRTL]);

  const openMessenger = useCallback(() => {
    setShowContactSheet(false);
    if (!seller?.messenger_link) return;
    const link = seller.messenger_link.startsWith('http')
      ? seller.messenger_link
      : `https://m.me/${seller.messenger_link}`;
    window.open(link, '_blank');
  }, [seller]);

  const submitReport = async () => {
    if (!reportReason) return;
    setSubmittingReport(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_type: 'book', item_id: bookId, reason: reportReason, details: reportDetails })
      });
      if (res.status === 409) {
        toast.info(isRTL ? 'لقد قمت بالإبلاغ عن هذا الإعلان مسبقا' : 'You have already reported this listing');
      } else if (res.ok) {
        toast.success(isRTL
          ? 'تم إرسال البلاغ بنجاح. سيقوم فريقنا بمراجعته.'
          : 'Report submitted successfully. Our team will review it.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (isRTL ? 'خطأ في إرسال البلاغ' : 'Error submitting report'));
      }
      setShowReport(false);
      setShowReportConfirm(false);
      setReportReason('');
      setReportDetails('');
    } catch (e) {
      console.error(e);
      toast.error(isRTL ? 'خطأ في الاتصال' : 'Connection error');
    }
    setSubmittingReport(false);
  };

  const handleBack = () => {
    // Pop real history when it exists (preserves list state/scroll). Honour
    // returnTo / section landing ONLY for deep links with no in-app history.
    if (hasInAppHistory()) return navigate(-1);
    navigate(location.state?.returnTo || '/student-hub/books', { replace: true });
  };

  const getLevelLabel = (key) => {
    const m = { primary: { en: 'Primary', ar: 'أساسي' }, secondary: { en: 'Secondary', ar: 'ثانوي' }, university: { en: 'University', ar: 'جامعي' }, postgrad: { en: 'Postgrad', ar: 'دراسات عليا' }, vocational: { en: 'Vocational', ar: 'مهني' }, other: { en: 'Other', ar: 'أخرى' } };
    return m[key] ? (isRTL ? m[key].ar : m[key].en) : key;
  };

  const getCityLabel = (key) => {
    const m = { khartoum: { en: 'Khartoum', ar: 'الخرطوم' }, omdurman: { en: 'Omdurman', ar: 'أم درمان' }, khartoum_north: { en: 'Khartoum North', ar: 'بحري' }, port_sudan: { en: 'Port Sudan', ar: 'بورتسودان' }, wad_madani: { en: 'Wad Madani', ar: 'ود مدني' }, kassala: { en: 'Kassala', ar: 'كسلا' } };
    return m[key] ? (isRTL ? m[key].ar : m[key].en) : key;
  };

  const getConditionLabel = (c) => {
    const m = { new: { en: 'New', ar: 'جديد' }, used_like_new: { en: 'Like New', ar: 'كالجديد' }, used_good: { en: 'Good', ar: 'جيد' }, used_ok: { en: 'Acceptable', ar: 'مقبول' }, used: { en: 'Used', ar: 'مستعمل' }, used_fair: { en: 'Fair', ar: 'مقبول' } };
    return m[c] ? (isRTL ? m[c].ar : m[c].en) : c;
  };

  // Placeholder gradient
  const COLORS = [['#3B82F6','#1D4ED8'],['#8B5CF6','#6D28D9'],['#10B981','#047857'],['#F59E0B','#D97706'],['#EF4444','#DC2626'],['#EC4899','#BE185D'],['#06B6D4','#0E7490']];
  const getGradient = (t) => COLORS[(t||'B').charCodeAt(0) % COLORS.length];
  const getInitials = (t) => { if(!t) return '?'; const w = t.split(/\s+/).filter(Boolean); return w.length >= 2 ? (w[0][0]+w[1][0]).toUpperCase() : w[0].substring(0,2).toUpperCase(); };

  if (loading) {
    return (
      <div className="book-details-page-v2" dir={isRTL ? 'rtl' : 'ltr'}>
        <AppHeader
          title={t('bookdeta.bookDetails')}
          onBack={handleBack}
          testId="book-details-header"
        />
        <div className="book-details-media"><Skeleton className="w-full aspect-[4/3]" /></div>
        <div className="book-details-body">
          <Skeleton className="h-8 w-3/4 mb-2" /><Skeleton className="h-5 w-1/2 mb-4" />
          <Skeleton className="h-10 w-32 mb-4" />
          <div className="flex gap-2 mb-4"><Skeleton className="h-7 w-20" /><Skeleton className="h-7 w-16" /></div>
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (!book) return null;

  const mode = book.mode || 'sell';
  const typeConfig = TYPE_CONFIG[mode] || TYPE_CONFIG.sell;
  const isFree = mode === 'free' || book.price === 0;
  const itemType = book.item_type; // 'digital' or 'physical'

  // Determine CTA label based on item_type
  let ctaConfig;
  if (isFree && itemType === 'digital') {
    ctaConfig = CTA_CONFIG.download_free;
  } else if (isFree && itemType === 'physical') {
    ctaConfig = CTA_CONFIG.contact_seller;
  } else {
    ctaConfig = CTA_CONFIG[mode] || CTA_CONFIG.sell;
  }
  const primaryLevel = Array.isArray(book.level) ? book.level[0] : book.level;
  const contentTypeLabel = getContentTypeLabel(book, isRTL);
  const title = isRTL && book.title_ar ? book.title_ar : book.title;
  const [gStart, gEnd] = getGradient(title);
  const views = metrics?.views || book.views7d || book.views || 0;
  const saves = metrics?.saves || book.saves7d || book.saves || 0;
  const hasPreview = book.preview_images && book.preview_images.length > 0;
  const DIGITAL_TYPES = ['notes', 'past_exam', 'lecture_notes', 'slides', 'lab_manual', 'summary'];
  const isDigitalContent = DIGITAL_TYPES.includes(book.type) || mode === 'free' || mode === 'download';
  
  // Use watermarked previews for paid content, originals for free content
  const isPaid = mode === 'sell' || mode === 'rent';
  const displayPreviews = hasPreview
    ? (isPaid && book.preview_images_watermarked?.length > 0
        ? book.preview_images_watermarked
        : book.preview_images)
    : [];

  return (
    <div className="book-details-page-v2" dir={isRTL ? 'rtl' : 'ltr'} data-testid="book-details">
      <AppHeader
        title={t('bookdeta.bookDetails')}
        onBack={handleBack}
        testId="book-details-header"
        rightSlot={
          <div className="header-actions" style={{ display: 'flex', gap: 6 }}>
            <button className="header-btn" onClick={handleShare} aria-label="Share" data-testid="share-btn">
              <Share2 size={18} />
            </button>
            <button className={`header-btn heart ${isSaved ? 'saved' : ''}`} onClick={toggleSave} aria-label="Save" data-testid="save-btn">
              <Heart size={18} fill={isSaved ? 'currentColor' : 'none'} />
            </button>
          </div>
        }
      />

      {/* Media Section */}
      <section className="book-details-media">
        {book.images && book.images.length > 0 ? (
          <>
            <div className="media-main">
              <img src={book.images[activeImage]} alt={title} />
              <span className={`type-badge ${typeConfig.color}`}>
                {isRTL ? typeConfig.label_ar : typeConfig.label_en}
              </span>
            </div>
            {book.images.length > 1 && (
              <div className="media-thumbs">
                {book.images.map((img, idx) => (
                  <button key={idx} className={`thumb ${idx === activeImage ? 'active' : ''}`} onClick={() => setActiveImage(idx)}>
                    <img src={img} alt={`${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="media-placeholder-v2" style={{ background: `linear-gradient(135deg, ${gStart}, ${gEnd})` }}>
            <span className="media-placeholder-initials">{getInitials(title)}</span>
            <FileText size={20} style={{ opacity: 0.6, color: 'white' }} />
            <span className="media-placeholder-type">{contentTypeLabel}</span>
            <span className={`type-badge ${typeConfig.color}`}>
              {isRTL ? typeConfig.label_ar : typeConfig.label_en}
            </span>
          </div>
        )}
      </section>

      {/* Body Content */}
      <section className="book-details-body">
        {/* Content type + engagement row */}
        <div className="detail-meta-row">
          <Badge variant="secondary" className="content-type-badge-detail" data-testid="content-type-badge">
            <FileText size={12} /> {contentTypeLabel}
          </Badge>
          {(views > 0 || saves > 0) && (
            <div className="detail-engagement" data-testid="detail-engagement">
              {views > 0 && <span className="engagement-stat"><Eye size={13} /> {views}</span>}
              {saves > 0 && <span className="engagement-stat"><Bookmark size={13} /> {saves}</span>}
            </div>
          )}
        </div>

        <h2 className="book-title-v2">{title}</h2>
        {book.author && <p className="book-author-v2">{book.author}</p>}

        {/* Price Block */}
        <div className="price-block">
          <span className={`price-value-v2 ${isFree ? 'free' : ''}`}>
            {isFree ? (isRTL ? 'مجاني' : 'Free') : formatMoney(book.price, book.currency || 'SDG', lang)}
          </span>
          {mode === 'rent' && book.rent_unit && <span className="rent-period">/ {t('unitWeek')}</span>}
        </div>

        {/* Attribute Chips */}
        <div className="attribute-chips">
          {primaryLevel && <span className="attr-chip level">{getLevelLabel(primaryLevel)}</span>}
          {book.tags?.[0] && (
            <span className="attr-chip subject">
              {getTagLabel(book.tags[0], isRTL)}
            </span>
          )}
          {book.condition && <span className="attr-chip condition">{getConditionLabel(book.condition)}</span>}
          {(book.city || book.location) && (
            <span className="attr-chip location"><MapPin size={12} />{getCityLabel(book.city || book.location)}</span>
          )}
        </div>

        {/* Description */}
        {book.description && (
          <div className="description-block" data-testid="description-block">
            <h3>{t('bookdeta.description')}</h3>
            <p>{isRTL && book.description_ar ? book.description_ar : book.description}</p>
          </div>
        )}

        {/* Preview / Sample Section */}
        {hasPreview ? (
          <div className="preview-section" data-testid="preview-section">
            <h3 className="preview-section-title">
              <Eye size={16} /> {t('bookdeta.previewPages')}
            </h3>
            <div className="preview-thumbnails">
              {displayPreviews.map((img, idx) => (
                <button
                  key={idx}
                  className="preview-thumb-btn"
                  onClick={() => { setPreviewIdx(idx); setPreviewOpen(true); }}
                  data-testid={`preview-thumb-${idx}`}
                >
                  <img src={img} alt={`${t('bookdeta.preview')} ${idx + 1}`} />
                  <span className="preview-thumb-overlay">
                    <ZoomIn size={18} />
                  </span>
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              className="view-sample-btn"
              onClick={() => { setPreviewIdx(0); setPreviewOpen(true); }}
              data-testid="view-sample-btn"
            >
              <Eye size={16} /> {t('bookdeta.viewSample')}
            </Button>
          </div>
        ) : isDigitalContent ? (
          <p className="no-preview-text" data-testid="no-preview-text">{t('bookdeta.noPreview')}</p>
        ) : null}

        {/* Seller Card — New SellerInfoCard Component */}
        {seller && (
          <>
            <SellerInfoCard
              seller={seller}
              onMessageClick={handleMessageSeller}
              onViewOtherListings={toggleSellerListings}
              listingsExpanded={showSellerListings}
            />

            {/* Inline "More from this seller" section */}
            <div ref={sellerListingsRef}>
              {loadingSellerListings && (
                <div className="seller-listings-loading" data-testid="seller-listings-loading">
                  <Skeleton className="h-20 w-full rounded-xl" />
                </div>
              )}
              {showSellerListings && !loadingSellerListings && (
                <div className="seller-listings-section" data-testid="seller-listings-section">
                  <div className="seller-listings-header">
                    <h3>{t('bookdeta.moreFromSeller')}</h3>
                    <button
                      className="seller-listings-collapse"
                      onClick={() => setShowSellerListings(false)}
                      data-testid="collapse-seller-listings"
                    >
                      <ChevronUp size={16} />
                      <span>{t('bookdeta.showLess')}</span>
                    </button>
                  </div>
                  {sellerListings.length > 0 ? (
                    <div className="seller-listings-grid">
                      {sellerListings.map(item => (
                        <button
                          key={item.id}
                          className="seller-listing-card"
                          onClick={() => navigate(`/student-hub/books/${item.id}`)}
                          data-testid={`seller-listing-${item.id}`}
                        >
                          <div
                            className="seller-listing-thumb"
                            style={{ background: `linear-gradient(135deg, ${getGradient(item.title)[0]}, ${getGradient(item.title)[1]})` }}
                          >
                            <span>{getInitials(item.title)}</span>
                          </div>
                          <div className="seller-listing-info">
                            <span className="seller-listing-name">
                              {isRTL && item.title_ar ? item.title_ar : item.title}
                            </span>
                            <span className="seller-listing-price">
                              {(item.mode === 'free' || item.price === 0)
                                ? (isRTL ? 'مجاني' : 'Free')
                                : formatMoney(item.price, item.currency || 'SDG', lang)}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="seller-listings-empty">{t('bookdeta.noOtherListings')}</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Rating Section */}
        <div className="rating-section" data-testid="rating-section">
          <h3 className="rating-section-title">
            <Star size={16} /> {t('bookdeta.rateThisBook')}
            {ratingStats.count > 0 && (
              <span className="rating-section-avg">
                {ratingStats.average.toFixed(1)} ({ratingStats.count})
              </span>
            )}
          </h3>
          
          {user ? (
            <div className="rating-input-row" data-testid="rating-input">
              <div className="star-input">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    className={`star-btn ${star <= (hoverStar || userRating) ? 'filled' : ''}`}
                    onClick={() => setUserRating(star)}
                    onMouseEnter={() => setHoverStar(star)}
                    onMouseLeave={() => setHoverStar(0)}
                    data-testid={`star-${star}`}
                  >
                    <Star size={24} fill={star <= (hoverStar || userRating) ? '#F59E0B' : 'none'} stroke="#F59E0B" />
                  </button>
                ))}
              </div>
              {userRating > 0 && (
                <>
                  <Textarea
                    value={userReview}
                    onChange={(e) => setUserReview(e.target.value)}
                    placeholder={t('bookdeta.writeReview')}
                    rows={2}
                    className="review-textarea"
                    maxLength={300}
                  />
                  <Button
                    onClick={submitRating}
                    disabled={submittingRating}
                    className="submit-rating-btn"
                    data-testid="submit-rating-btn"
                  >
                    {submittingRating ? '...' : t('bookdeta.submitRating')}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <Button variant="outline" onClick={() => navigate('/welcome')} className="login-to-rate-btn">
              {t('bookdeta.loginToRate')}
            </Button>
          )}

          {/* Reviews List */}
          {ratings.length > 0 && (
            <div className="reviews-list" data-testid="reviews-list">
              <h4 className="reviews-list-title">{t('bookdeta.recentReviews')} ({ratings.length})</h4>
              {ratings.slice(0, 5).map((r, idx) => (
                <div key={idx} className="review-item">
                  <div className="review-header">
                    <span className="review-author">{r.user_name}</span>
                    <span className="review-stars">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={12} fill={s <= r.rating ? '#F59E0B' : 'none'} stroke="#F59E0B" />
                      ))}
                    </span>
                  </div>
                  {r.review && <p className="review-text">{r.review}</p>}
                  {r.created_at && (
                    <span className="review-date">
                      {new Date(r.created_at).toLocaleDateString(isRTL ? 'ar-SD' : 'en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Posted Date */}
        {book.createdAt && (
          <p className="posted-date-v2">
            {isRTL ? 'نشر بتاريخ' : 'Posted'}: {new Date(book.createdAt).toLocaleDateString(isRTL ? 'ar-SD' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
          </p>
        )}

        {/* Report Link */}
        {user && (
          <button className="report-link" onClick={() => setShowReport(true)} data-testid="report-btn">
            <Flag size={14} />
            {t('bookdeta.reportThisListing')}
          </button>
        )}

        {/* Related Books */}
        {relatedBooks.length > 0 && (
          <div className="related-section" data-testid="related-books">
            <h3 className="related-title">{isRTL ? 'كتب مشابهة' : 'Related Books'}</h3>
            <div className="related-grid">
              {relatedBooks.map(rb => (
                <button
                  key={rb.id}
                  className="related-card"
                  onClick={() => navigate(`/student-hub/books/${rb.id}`)}
                  data-testid={`related-${rb.id}`}
                >
                  <div className="related-thumb" style={{ background: `linear-gradient(135deg, ${getGradient(rb.title)[0]}, ${getGradient(rb.title)[1]})` }}>
                    <span>{getInitials(rb.title)}</span>
                  </div>
                  <div className="related-info">
                    <span className="related-name">{isRTL && rb.title_ar ? rb.title_ar : rb.title}</span>
                    <span className="related-price">
                      {(rb.mode === 'free' || rb.price === 0) ? (isRTL ? 'مجاني' : 'Free') : formatMoney(rb.price, rb.currency || 'SDG', lang)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Sticky CTA Bar */}
      <footer className="book-details-cta-v2">
        {isOwner ? (
          <Button className="cta-primary blue" onClick={() => setShowContactEditor(true)} data-testid="edit-contacts-btn">
            <Pencil size={16} />
            {t('bookdeta.editContacts')}
          </Button>
        ) : (
          <>
            <Button className={`cta-primary ${isFree ? 'green' : 'blue'}`} onClick={handleCTA} data-testid="primary-cta">
              {isFree && itemType === 'digital' && <Download size={16} />}
              {isFree && itemType === 'physical' && <MessageCircle size={16} />}
              {isRTL ? ctaConfig.label_ar : ctaConfig.label_en}
            </Button>
            {hasCall && (
              <Button variant="outline" className="cta-secondary" onClick={openCall} data-testid="cta-call">
                <Phone size={18} />{t('bookdeta.call')}
              </Button>
            )}
            {hasWhatsApp && (
              <Button variant="outline" className="cta-secondary whatsapp" onClick={openWhatsApp} data-testid="cta-whatsapp">
                <MessageCircle size={18} />
              </Button>
            )}
          </>
        )}
      </footer>

      {/* Owner: edit contact methods */}
      {isOwner && (
        <BookContactEditor
          bookId={book.book_id}
          open={showContactEditor}
          onClose={() => setShowContactEditor(false)}
          onSaved={(newContact) => { if (newContact) setContact(newContact); setShowContactEditor(false); }}
        />
      )}

      {/* Report Modal */}
      <Dialog open={showReport} onOpenChange={(open) => { if (!open) { setShowReport(false); setShowReportConfirm(false); } else { setShowReport(true); } }}>
        <DialogContent dir={isRTL ? 'rtl' : 'ltr'}>
          {!showReportConfirm ? (
            <>
              <DialogHeader><DialogTitle>{t('bookdeta.reportListing')}</DialogTitle></DialogHeader>
              <div className="report-form-v2">
                <div className="form-group">
                  <label>{t('bookdeta.reason')} <span style={{color:'#EF4444'}}>*</span></label>
                  <Select value={reportReason} onValueChange={setReportReason}>
                    <SelectTrigger data-testid="report-reason-select"><SelectValue placeholder={t('bookdeta.selectReason')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scam_fraud">{t('bookdeta.scamFraud')}</SelectItem>
                      <SelectItem value="wrong_category">{t('bookdeta.wrongCategory')}</SelectItem>
                      <SelectItem value="spam">{t('bookdeta.spam')}</SelectItem>
                      <SelectItem value="offensive_content">{t('bookdeta.offensiveContent')}</SelectItem>
                      <SelectItem value="fake_item">{t('bookdeta.fakeItem')}</SelectItem>
                      <SelectItem value="other">{t('bookdeta.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {reportReason === 'fake_item' && (
                    <p className="report-warning-note" data-testid="fake-item-warning">
                      {t('bookdeta.fakeItemWarning')}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label>{t('bookdeta.additionalNote')}</label>
                  <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder={t('bookdeta.addNote')} rows={3} data-testid="report-note-input" />
                </div>
                <div className="report-false-warning" data-testid="report-false-warning">
                  <span className="report-false-warning__icon">&#9888;&#65039;</span>
                  <span>{t('bookdeta.reportFalseWarning')}</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowReport(false)}>{t('bam.cancel')}</Button>
                <Button onClick={() => setShowReportConfirm(true)} disabled={!reportReason} data-testid="report-submit-btn">
                  {t('bookdeta.submit')}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader><DialogTitle>{t('bookdeta.confirmReport')}</DialogTitle></DialogHeader>
              <p className="report-confirm-msg" data-testid="report-confirm-dialog">{t('bookdeta.confirmReportMsg')}</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowReportConfirm(false)}>{t('bam.cancel')}</Button>
                <Button onClick={submitReport} disabled={submittingReport} data-testid="report-confirm-submit">
                  {submittingReport ? t('bookdeta.submitting') : t('bookdeta.yesSubmit')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact Options Sheet */}
      <Dialog open={showContactSheet} onOpenChange={setShowContactSheet}>
        <DialogContent dir={isRTL ? 'rtl' : 'ltr'} className="contact-sheet-content">
          <DialogHeader>
            <DialogTitle>{t('bookdeta.contactOptions')}</DialogTitle>
          </DialogHeader>
          <div className="contact-sheet-options" data-testid="contact-options-sheet">
            {hasChat && (
              <button
                className="contact-option-btn contact-option--wassla"
                onClick={openWasslaChat}
                data-testid="contact-option-wassla"
              >
                <MessageCircle size={20} />
                <span>{t('bookdeta.chatInWassla')}</span>
              </button>
            )}

            {hasCall && (
              <button
                className="contact-option-btn contact-option--call"
                onClick={openCall}
                data-testid="contact-option-call"
              >
                <Phone size={20} />
                <span>{t('bookdeta.call')}</span>
                <span className="contact-option-value" dir="ltr">{callDisplay}</span>
              </button>
            )}

            {hasWhatsApp && (
              <button
                className="contact-option-btn contact-option--whatsapp"
                onClick={openWhatsApp}
                data-testid="contact-option-whatsapp"
              >
                <Phone size={20} />
                <span>{t('bookdeta.whatsapp')}</span>
                <span className="contact-option-value" dir="ltr">{waDisplay}</span>
              </button>
            )}

            {hasEmail && (
              <button
                className="contact-option-btn contact-option--email"
                onClick={openEmail}
                data-testid="contact-option-email"
              >
                <Mail size={20} />
                <span>{t('bookdeta.email')}</span>
                <span className="contact-option-value" dir="ltr">{contactInfo.email}</span>
              </button>
            )}

            {seller?.messenger_link && (
              <button
                className="contact-option-btn contact-option--messenger"
                onClick={openMessenger}
                data-testid="contact-option-messenger"
              >
                <MessageCircle size={20} />
                <span>{t('bookdeta.messenger')}</span>
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Lightbox */}
      {hasPreview && previewOpen && (
        <div className="preview-lightbox" data-testid="preview-lightbox" onClick={() => setPreviewOpen(false)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setPreviewOpen(false)} data-testid="lightbox-close">
              <X size={22} />
            </button>
            <div className="lightbox-counter">
              {previewIdx + 1} {t('bookdeta.previewOf')} {displayPreviews.length}
            </div>
            <img src={displayPreviews[previewIdx]} alt={`${t('bookdeta.preview')} ${previewIdx + 1}`} className="lightbox-image" />
            {displayPreviews.length > 1 && (
              <div className="lightbox-nav">
                <button
                  className="lightbox-arrow prev"
                  disabled={previewIdx === 0}
                  onClick={() => setPreviewIdx(i => Math.max(0, i - 1))}
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  className="lightbox-arrow next"
                  disabled={previewIdx === displayPreviews.length - 1}
                  onClick={() => setPreviewIdx(i => Math.min(displayPreviews.length - 1, i + 1))}
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
